import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Globe, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { sitesList } from '@/shared/api/generated/sites/sites';
import { tenantsList } from '@/shared/api/generated/tenants/tenants';
import { useSession } from '@/shared/auth';
import { cx } from '@/shared/lib/cx';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';

import styles from './CommandPalette.module.scss';

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: ReactNode;
  group: string;
  run: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Палитра команд.
 *
 * Открывается по ⌘K и по кнопке в шапке. Ищет не только по разделам, но и по
 * тенантам с сайтами: список разделов человек и так помнит, а вот нужный
 * сайт среди сотни — нет, и именно ради этого палитрой начинают пользоваться.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  // Сочетание слушается на документе, а не на поле: палитру открывают
  // откуда угодно, в том числе когда фокус в таблице.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <Body
          onClose={() => {
            onOpenChange(false);
          }}
        />
      )}
    </Dialog>
  );
}

function Body({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const session = useSession();

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const listRef = useRef<HTMLUListElement>(null);
  const search = useDebounced(query, 200);

  const isSuperAdmin = session.status === 'authenticated' && session.user.is_super_admin;

  // Запрос уходит только когда есть что искать: пустая палитра не должна
  // дёргать сервер при каждом открытии.
  const tenants = useQuery({
    queryKey: ['palette', 'tenants', search],
    enabled: search.length > 1 && isSuperAdmin,
    queryFn: async () => {
      const response = await tenantsList({ search, per_page: 5 });

      return response.status === 200 ? response.data.items : [];
    },
  });

  const sites = useQuery({
    queryKey: ['palette', 'sites', search],
    enabled: search.length > 1,
    queryFn: async () => {
      const response = await sitesList({ search, per_page: 5 });

      return response.status === 200 ? response.data.items : [];
    },
  });

  const commands = useMemo<Command[]>(() => {
    const go = (to: '/tenants' | '/sites') => () => {
      void navigate({ to });
      onClose();
    };

    const navigation: Command[] = [
      {
        id: 'nav-sites',
        label: 'Сайты',
        icon: <Globe size={15} />,
        group: 'Разделы',
        run: go('/sites'),
      },
    ];

    if (isSuperAdmin) {
      navigation.unshift({
        id: 'nav-tenants',
        label: 'Тенанты',
        icon: <Building2 size={15} />,
        group: 'Разделы',
        run: go('/tenants'),
      });
    }

    const matched = navigation.filter((command) =>
      command.label.toLowerCase().includes(query.trim().toLowerCase()),
    );

    const found: Command[] = [
      ...(tenants.data ?? []).map((tenant) => ({
        id: `tenant-${tenant.id}`,
        label: tenant.name,
        hint: tenant.slug,
        icon: <Building2 size={15} />,
        group: 'Тенанты',
        run: () => {
          void navigate({ to: '/tenants' });
          onClose();
        },
      })),
      ...(sites.data ?? []).map((site) => ({
        id: `site-${site.id}`,
        label: site.name,
        hint: `${site.tenant_name} · ${site.slug}`,
        icon: <Globe size={15} />,
        group: 'Сайты',
        run: () => {
          void navigate({ to: '/sites/$siteId/$env', params: { siteId: site.id, env: 'prod' } });
          onClose();
        },
      })),
    ];

    return [...matched, ...found];
  }, [query, tenants.data, sites.data, isSuperAdmin, navigate, onClose]);

  const move = (delta: number) => {
    setActive((current) => {
      if (commands.length === 0) {
        return 0;
      }

      // Список закольцован: с последнего пункта стрелка вниз ведёт на первый.
      const next = (current + delta + commands.length) % commands.length;
      listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });

      return next;
    });
  };

  // Список мог укоротиться между перерисовками — например, пока ехал ответ
  // поиска. Подсветка приводится к границам здесь, а не отдельным
  // состоянием: одно значение правды вместо двух, которые нужно сверять.
  const activeIndex = Math.min(active, Math.max(commands.length - 1, 0));

  let lastGroup = '';

  return (
    <DialogContent
      title="Поиск и переходы"
      titleHidden
      size="md"
      className={styles['palette']}
      onOpenAutoFocus={(event) => {
        // Фокус уводится в поле, а не на первый пункт: палитру открывают,
        // чтобы печатать.
        event.preventDefault();
        (event.currentTarget as HTMLElement).querySelector('input')?.focus();
      }}
    >
      <div className={styles['field']}>
        <Search size={16} className={styles['field__icon']} />
        <input
          className={styles['field__input']}
          placeholder="Раздел, тенант или сайт"
          aria-label="Поиск по админке"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            // Подсветка возвращается на первый пункт: иначе после набора
            // буквы она остаётся на позиции, где теперь другой пункт, и
            // Enter выполняет не то, что человек видит.
            setActive(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              commands[activeIndex]?.run();
            }
          }}
        />
      </div>

      <ul className={styles['list']} ref={listRef}>
        {commands.map((command, index) => {
          const showGroup = command.group !== lastGroup;
          lastGroup = command.group;

          return (
            <li key={command.id}>
              {showGroup && <p className={styles['group']}>{command.group}</p>}

              <button
                type="button"
                className={cx(styles['item'], index === activeIndex && styles['item--active'])}
                onMouseEnter={() => {
                  setActive(index);
                }}
                onClick={command.run}
              >
                <span className={styles['item__icon']} aria-hidden="true">
                  {command.icon}
                </span>
                <span className={styles['item__label']}>{command.label}</span>
                {command.hint !== undefined && (
                  <span className={styles['item__hint']}>{command.hint}</span>
                )}
              </button>
            </li>
          );
        })}

        {commands.length === 0 && (
          <li className={styles['empty']}>
            {query.trim() === '' ? 'Начните печатать' : 'Ничего не нашлось'}
          </li>
        )}
      </ul>
    </DialogContent>
  );
}
